import { Component, Input, OnInit, HostListener, ElementRef } from '@angular/core';
import { ModalController, ToastController, AlertController } from '@ionic/angular';
import { Trabajo } from '../../models/trabajo';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { Metodosui } from '../../services/metodosui';
import { firstValueFrom } from 'rxjs';
import { AdminCrudPage } from '../../pages/ajustes/admin-crud/admin-crud.page';

// Interface for line items
interface TrabajoItem {
  id: number;  // 0 for new items, >0 for existing
  medida: string;
  cantidad: number;
  descripcion: string;
}

@Component({
  selector: 'app-trabajosedit',
  templateUrl: './trabajosedit.component.html',
  styleUrls: ['./trabajosedit.component.scss'],
  standalone: false
})
export class TrabajoseditComponent implements OnInit {

  @Input() trabajo: Trabajo | null = null;
  @Input() mode: 'add' | 'edit' = 'add';
  @Input() initialItems: any[] = [];

  currentStep: number = 1;

  // Form data (Step 1)
  @Input() formData = {
    cliente: '',
    lugar: '',
    solicitante: '',
    incluyeMaterial: true, // Y/N toggle
    estado: 'No iniciado',
    descripcion: ''
  };
  // Current item being added (Step 2)
  currentItem: TrabajoItem = {
    id: 0,
    medida: 'unidad',
    cantidad: 1,
    descripcion: ''
  };

  // List of items added
  items: TrabajoItem[] = [];

  // Tipo de medida options (values match DB)
  tipoMedidaOptions = [
    { label: 'Unidad', value: 'unidad' },
    { label: 'M', value: 'm' },
    { label: 'M²', value: 'm2' }
  ];

  // Autocomplete state
  clientesList: any[] = [];
  filteredClientes: any[] = [];
  showClienteDropdown = false;
  selectedClienteId: number = 0;

  direccionesList: any[] = [];
  filteredDirecciones: any[] = [];
  showDireccionDropdown = false;
  selectedDireccionId: number = 0;

  solicitantesList: any[] = [];
  filteredSolicitantes: any[] = [];
  showSolicitanteDropdown = false;
  selectedSolicitanteId: number = 0;

  showMedidaDropdown: boolean = false;
  isEditingItem: boolean = false;

  // New auxiliary lists for Llenardatos creation
  regionesList: any[] = [];
  gruposList: any[] = [];
  isSaving: boolean = false;
  isKeyboardVisible = false;
  private initialHeight = window.innerHeight;

  constructor(
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui,
    private el: ElementRef
  ) { }

  async ngOnInit() {
    // Fetch autocomplete lists
    await this.loadClientesList();
    await this.loadSolicitantesList();
    this.loadAuxiliaryData(); // Async but no need to wait for start

    if (this.mode === 'add' && this.initialItems && this.initialItems.length > 0) {
      this.items = this.initialItems.map(item => ({
        id: 0,
        medida: item.dt3 ?? 'unidad',
        cantidad: Number(item.dt4) || 1,
        descripcion: item.dt2 ?? ''
      }));
      this.updateDescription();
    }

    if (this.mode === 'edit' && this.trabajo) {
      this.formData = {
        cliente: this.trabajo.cliente,
        lugar: this.trabajo.lugar,
        solicitante: this.trabajo.solicitante,
        estado: this.trabajo.estado,
        incluyeMaterial: (this.trabajo as any).incluye_material === 'Y',
        descripcion: this.trabajo.descripcion || ''
      };
      await this.loadItems(this.trabajo.id);
    }

    // Resolve IDs from names if pre-filled (covers both Add from Cot y Edit)
    if (this.formData.cliente) {
      const match = this.clientesList.find(c => c.nombre.toLowerCase() === this.formData.cliente.toLowerCase());
      if (match) {
        this.selectedClienteId = match.id;
        await this.loadDireccionesByCliente(match.id);
        if (this.formData.lugar) {
          const dMatch = this.direccionesList.find(d => d.nombre.toLowerCase() === this.formData.lugar.toLowerCase());
          if (dMatch) this.selectedDireccionId = dMatch.id;
        }
      }
    }
    if (this.formData.solicitante) {
      const sMatch = this.solicitantesList.find(s => s.nombre.toLowerCase() === this.formData.solicitante.toLowerCase());
      if (sMatch) this.selectedSolicitanteId = sMatch.id;
    }
  }

  async loadItems(trabajoId: number) {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "genModel", "trabajosItems", String(trabajoId), '', '', '', '', '', '', '', '', '', '', '', '', '', ''
          )
        )
      );
      // Map backend data to items array
      // Assuming: dt1=id, dt2=descripcion, dt3=medida, dt4=cantidad
      if (Array.isArray(data)) {
        this.items = data.map(item => ({
          id: Number(item.dt1 || item.id) || 0,
          medida: item.dt3 || item.medida || 'unidad',
          cantidad: Number(item.dt4 || item.cantidad) || 1,
          descripcion: item.dt2 || item.descripcion || ''
        }));
      }
    } catch (e) {
      console.error('Error loading items:', e);
    }
  }

  // ── Lists Loading ────────────────────────────────────────────────────────
  async loadClientesList() {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral('selDtsList', 'listClientes', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '')
        )
      );
      if (Array.isArray(data)) {
        this.clientesList = data.map(c => ({ id: Number(c.dt1), nombre: c.dt2 }));

        // Sort alphabetically first
        this.clientesList.sort((a, b) => a.nombre.localeCompare(b.nombre));

        // Prioritize "Inmobiliaria Don Antonio"
        const index = this.clientesList.findIndex(c => c.nombre.trim().toLowerCase() === 'inmobiliaria don antonio');
        if (index > -1) {
          const prioritized = this.clientesList.splice(index, 1)[0];
          this.clientesList.unshift(prioritized);
        }

        // Map ID back from name
        if (!this.selectedClienteId && this.formData.cliente) {
          const match = this.clientesList.find(c => c.nombre.toLowerCase().trim() === this.formData.cliente.toLowerCase().trim());
          if (match) {
            this.selectedClienteId = match.id;
            this.loadDireccionesByCliente(match.id);
          }
        }

        this.filteredClientes = [...this.clientesList];
      }
    } catch (e) { console.error('Error loading clientes', e); }
  }

  async loadDireccionesByCliente(clienteId: number) {
    if (!clienteId) return;
    try {
      const data: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral('selDtsList', 'listLugaresAll', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '')
        )
      );
      if (Array.isArray(data)) {
        const all = data.map(c => ({ id: Number(c.dt1), nombre: c.dt2, id_cliente: Number(c.dt3) }));
        this.direccionesList = all.filter(d => d.id_cliente === clienteId);
        this.filteredDirecciones = [...this.direccionesList];

        if (!this.selectedDireccionId && this.formData.lugar) {
          const match = this.direccionesList.find(d => d.nombre.toLowerCase().trim() === this.formData.lugar.toLowerCase().trim());
          if (match) {
            this.selectedDireccionId = match.id;
          }
        }
      } else {
        this.direccionesList = [];
        this.filteredDirecciones = [];
      }
    } catch (e) { console.error('Error loading direcciones', e); }
  }

  async loadSolicitantesList() {
    try {
      const data: any = await firstValueFrom(
        this.javapiService.executeAdminCrud('SOLICITANTE', 'SELECT_ALL', { dt1: '' })
      );
      if (Array.isArray(data)) {
        this.solicitantesList = data.map(c => ({ 
          id: Number(c.dt1), 
          nombre: c.dt2,
          clienteId: Number(c.dt6) // Corrected from dt3 to dt6 using full Admin CRUD data
        }));

        if (!this.selectedSolicitanteId && this.formData.solicitante) {
          const match = this.solicitantesList.find(s => s.nombre.toLowerCase().trim() === this.formData.solicitante.toLowerCase().trim());
          if (match) {
            this.selectedSolicitanteId = match.id;
          }
        }

        this.filteredSolicitantes = [...this.solicitantesList];
      }
    } catch (e) { console.error('Error loading solicitantes', e); }
  }

  async loadAuxiliaryData() {
    // Load regions and notification groups for dynamic creation forms
    try {
      this.javapiService.executeAdminCrud('REGION', 'SELECT_ALL', { dt1: '' }).subscribe(res => this.regionesList = res);
      this.javapiService.executeAdminCrud('GRUPO', 'SELECT_ALL', { dt1: '' }).subscribe(res => this.gruposList = res);
    } catch (e) { console.error('Error loading auxiliary data', e); }
  }

  // ── Autocomplete: Cliente ────────────────────────────────────────────────
  filterClientes(event: any) {
    const q = (event.target?.value || '').toLowerCase();
    this.formData.cliente = event.target?.value || '';
    
    // Show list filtered by query, or all if empty
    this.filteredClientes = this.clientesList.filter(c => c.nombre.toLowerCase().includes(q));
    this.showClienteDropdown = true;
    
    // Close others
    this.showDireccionDropdown = false;
    this.showSolicitanteDropdown = false;

    // Clear related if text changed and was previously selected
    if (this.selectedClienteId && !this.filteredClientes.some(c => c.nombre.toLowerCase() === q)) {
      this.selectedClienteId = 0;
      this.selectedDireccionId = 0;
      this.direccionesList = [];
      this.filteredDirecciones = [];
      this.formData.lugar = '';
    }
  }

  selectCliente(c: any) {
    this.formData.cliente = c.nombre;
    this.selectedClienteId = c.id;
    this.showClienteDropdown = false;
    this.filteredClientes = [];
    this.formData.lugar = '';
    this.selectedDireccionId = 0;
    this.loadDireccionesByCliente(c.id);
  }

  clearCliente() {
    this.formData.cliente = '';
    this.selectedClienteId = 0;
    this.selectedDireccionId = 0;
    this.showClienteDropdown = false;
    this.filteredClientes = [];
    this.formData.lugar = '';
    this.direccionesList = [];
    this.filteredDirecciones = [];
    this.showDireccionDropdown = false;
  }

  async createCliente() {
    this.showClienteDropdown = false;
    const modal = await this.modalCtrl.create({
      component: AdminCrudPage,
      componentProps: { 
        entity: 'CLIENTE', 
        autoOpenAdd: true,
        initialData: { dt2: this.formData.cliente } 
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    
    if (data) {
      await this.loadClientesList();
      const newName = data.nombre || data.dt2 || this.formData.cliente;
      
      const match = this.clientesList.find(c => c.nombre.toLowerCase().trim() === newName.toLowerCase().trim());
      if (match) {
        this.selectCliente(match);
      } else {
        const newId = Number(data.id) || 0;
        this.selectCliente({ id: newId, nombre: newName });
      }
    }
  }

  doesClienteExist(name: string): boolean {
    if (!name) return false;
    const clean = name.trim().toLowerCase();
    return this.clientesList.some(c => c.nombre?.trim().toLowerCase() === clean);
  }

  // ── Autocomplete: Direccion (Lugar) ──────────────────────────────────────
  filterDirecciones(event: any) {
    if (!this.selectedClienteId) return;
    const q = (event.target?.value || '').toLowerCase();
    this.formData.lugar = event.target?.value || '';
    
    this.filteredDirecciones = this.direccionesList.filter(d => d.nombre.toLowerCase().includes(q));
    this.showDireccionDropdown = true;
    
    // Close others
    this.showClienteDropdown = false;
    this.showSolicitanteDropdown = false;
  }
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.closeAllDropdowns();
    } else {
      const target = event.target as HTMLElement;
      if (!target.closest('.autocomplete-wrapper')) {
        this.closeAllDropdowns();
      }
    }
  }

  closeAllDropdowns() {
    this.showClienteDropdown = false;
    this.showDireccionDropdown = false;
    this.showSolicitanteDropdown = false;
    this.showMedidaDropdown = false;
  }


  selectDireccion(d: any) {
    this.formData.lugar = d.nombre;
    this.selectedDireccionId = d.id;
    this.showDireccionDropdown = false;
    this.filteredDirecciones = [];
  }

  clearDireccion() {
    this.formData.lugar = '';
    this.selectedDireccionId = 0;
    this.showDireccionDropdown = false;
    this.filteredDirecciones = [];
  }

  async createDireccion() {
    if (!this.selectedClienteId) {
      this.metodosUi.showToast('Primero debes seleccionar un cliente', 'warning');
      return;
    }
    this.showDireccionDropdown = false;
    const modal = await this.modalCtrl.create({
      component: AdminCrudPage,
      componentProps: { 
        entity: 'DIRECCION', 
        autoOpenAdd: true,
        initialData: { 
          dt2: String(this.selectedClienteId), 
          dt5: this.formData.lugar 
        } 
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data) {
      await this.loadDireccionesByCliente(this.selectedClienteId);
      const newName = data.nombre || data.dt2 || this.formData.lugar;
      
      const match = this.direccionesList.find(d => d.nombre.toLowerCase().trim() === newName.toLowerCase().trim());
      if (match) {
        this.selectDireccion(match);
      } else {
        const newId = Number(data.id) || 0;
        this.selectDireccion({ id: newId, nombre: newName, id_cliente: this.selectedClienteId });
      }
    }
  }

  doesDireccionExist(name: string): boolean {
    if (!name) return false;
    const clean = name.trim().toLowerCase();
    return this.direccionesList.some(d => d.nombre?.trim().toLowerCase() === clean);
  }

  // ── Autocomplete: Solicitante ────────────────────────────────────────────
  filterSolicitantes(event: any) {
    const q = (event.target?.value || '').toLowerCase();
    this.formData.solicitante = event.target?.value || '';
    
    this.filteredSolicitantes = this.solicitantesList.filter(s => s.nombre.toLowerCase().includes(q));
    this.showSolicitanteDropdown = true;
    
    // Close others
    this.showClienteDropdown = false;
    this.showDireccionDropdown = false;
  }

  selectSolicitante(s: any) {
    this.formData.solicitante = s.nombre;
    this.selectedSolicitanteId = s.id;
    this.showSolicitanteDropdown = false;
    this.filteredSolicitantes = [];

    // Auto-select client if linked
    if (s.clienteId) {
      const client = this.clientesList.find(c => c.id === s.clienteId);
      if (client) {
        this.selectCliente(client);
      }
    }
  }

  clearSolicitante() {
    this.formData.solicitante = '';
    this.selectedSolicitanteId = 0;
    this.showSolicitanteDropdown = false;
    this.filteredSolicitantes = [];
  }

  async createSolicitante() {
    if (!this.selectedClienteId) {
      this.metodosUi.showToast('Primero debes seleccionar un cliente', 'warning');
      return;
    }
    this.showSolicitanteDropdown = false;
    const modal = await this.modalCtrl.create({
      component: AdminCrudPage,
      componentProps: { 
        entity: 'SOLICITANTE', 
        autoOpenAdd: true,
        initialData: { 
          dt6: String(this.selectedClienteId), 
          dt2: this.formData.solicitante 
        } 
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();

    if (data) {
      await this.loadSolicitantesList();
      const newName = data.nombre || data.dt2 || this.formData.solicitante;
      
      const match = this.solicitantesList.find(s => s.nombre.toLowerCase().trim() === newName.toLowerCase().trim());
      if (match) {
        this.selectSolicitante(match);
      } else {
        const newId = Number(data.id) || 0;
        this.selectSolicitante({ id: newId, nombre: newName });
      }
    }
  }

  doesSolicitanteExist(name: string): boolean {
    if (!name) return false;
    const clean = name.trim().toLowerCase();
    return this.solicitantesList.some(s => s.nombre?.trim().toLowerCase() === clean);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isKeyboardVisible = window.innerHeight < this.initialHeight * 0.8;
  }

  nextStep() {
    if (this.currentStep === 1) {
      if (!this.formData.cliente || !this.selectedClienteId) {
        this.metodosUi.showToast('Debes seleccionar un cliente válido de la lista', 'warning');
        return;
      }
      if (!this.formData.lugar || !this.selectedDireccionId) {
        this.metodosUi.showToast('Debes seleccionar una dirección válida de la lista', 'warning');
        return;
      }
      if (!this.formData.solicitante || !this.selectedSolicitanteId) {
        this.metodosUi.showToast('Debes seleccionar un solicitante válido de la lista', 'warning');
        return;
      }
    } else if (this.currentStep === 2) {
      if (this.items.length === 0) {
        this.metodosUi.showToast('Debes agregar al menos un item a la lista', 'warning');
        return;
      }
    }
    this.currentStep++;
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  async addItem() {
    if (!this.currentItem.descripcion?.trim() || this.currentItem.cantidad <= 0) {
      this.metodosUi.showToast('La descripción es obligatoria y cantidad debe ser mayor a 0', 'warning');
      return;
    }
    if (this.currentItem.medida && this.currentItem.cantidad > 0) {
      this.items.push({ ...this.currentItem });
      // Reset with id=0 for new items
      this.currentItem = { id: 0, medida: 'unidad', cantidad: 1, descripcion: '' };
      this.isEditingItem = false;
      this.selectedDescMode = null;
      this.originalDescText = '';
      this.improvedDescText = '';
      this.updateDescription();
    }
  }

  removeItem(index: number) {
    this.items.splice(index, 1);
    this.isEditingItem = false;
    this.selectedDescMode = null;
    this.originalDescText = '';
    this.improvedDescText = '';
    this.updateDescription();
  }

  editItem(index: number) {
    // Load item into form for editing (preserving the ID)
    const item = this.items[index];
    this.currentItem = { ...item };
    this.isEditingItem = true;
    this.selectedDescMode = null;
    this.originalDescText = '';
    this.improvedDescText = '';
    // Remove from list (will be re-added with same ID when user clicks "Agregar Item")
    this.items.splice(index, 1);
    this.updateDescription();
  }

  updateDescription() {
    const sortedItems = [...this.items].sort((a, b) => a.id - b.id);
    // Use 'Sin detalle' if description is empty, to match save logic
    this.formData.descripcion = sortedItems.map(item => `${item.descripcion || 'Sin detalle'}`).join('; ');
  }

  // ── Custom Dropdown: Medida ──────────────────────────────────────────────
  toggleMedidaDropdown() {
    this.showMedidaDropdown = !this.showMedidaDropdown;
  }

  selectMedida(value: string) {
    this.currentItem.medida = value;
    this.showMedidaDropdown = false;
  }

  getMedidaLabel(value: string): string {
    const opt = this.tipoMedidaOptions.find(o => o.value === value);
    return opt ? opt.label : 'Seleccionar';
  }

  close() {
    this.modalCtrl.dismiss();
  }

  async save() {
    // Ensure description is up to date
    this.updateDescription();

    const userJson = localStorage.getItem('user');
    let usuario = '';
    if (userJson) {
      const user = JSON.parse(userJson);
      usuario = user.l_usuario || '';
    }

    const payload = {
      trabajoD: {
        id: this.trabajo?.id ? String(this.trabajo.id) : '0',
        cliente: this.formData.cliente,
        lugar: this.formData.lugar,
        descripcion: this.formData.descripcion,
        solicitante: this.formData.solicitante,
        incluye_material: this.formData.incluyeMaterial ? 'Y' : 'N',
        estado: this.formData.estado
      },
      trabajoP: {
        rutina: 'mdf',
        subrutina: this.mode === 'add' ? 'insert' : 'update'
      },
      items: this.items,
      usuario: usuario
    };

    this.isSaving = true;
    try {
      const result = await firstValueFrom(this.javapiService.insertaTrabajo(payload));

      // Show success toast
      this.metodosUi.showToast('Trabajo guardado exitosamente', 'success');

      this.modalCtrl.dismiss({
        action: 'saved',
        data: result
      });
    } catch (e: any) {
      console.error('Error saving trabajo:', e);

      // Show error toast and stay in modal
      const errorMsg = e?.error?.error || e?.message || 'Error al guardar el trabajo';
      this.metodosUi.showToast(errorMsg, 'danger');
    } finally {
      this.isSaving = false;
    }
  }

  originalDescText: string = '';
  improvedDescText: string = '';
  selectedDescMode: 'original' | 'ai' | null = null;
  isImprovingDesc: boolean = false;
  aiAttemptStatus: string = 'Mejorar con IA';

  async mejorarDescripcionConIA() {
    if (!this.currentItem.descripcion || !this.currentItem.descripcion.trim()) {
      await this.metodosUi.showToast('Ingresa una descripción corta antes de mejorarla', 'warning');
      return;
    }

    // Guardar el texto original antes de llamar a la IA
    this.originalDescText = this.currentItem.descripcion;

    this.isImprovingDesc = true;
    this.aiAttemptStatus = 'Mejorando...';
    try {
      // 1. Obtener los parámetros de configuración de la BD
      const paramRes = await firstValueFrom(this.javapiService.getJsonParam('CONFIG_IA_MEJORAR_DESC'));
      let model = undefined;
      let temperature = undefined;
      let systemInstruction = undefined;
      let freeAttempts = 2;
      let payAttempts = 1;
      let forcePay = false;

      if (paramRes && paramRes.pa_json) {
        try {
          const config = typeof paramRes.pa_json === 'string' ? JSON.parse(paramRes.pa_json) : paramRes.pa_json;
          model = config.model;
          temperature = config.temperature;
          systemInstruction = config.system_instruction;
          
          if (config.free_attempts !== undefined) freeAttempts = Number(config.free_attempts);
          if (config.pay_attempts !== undefined) payAttempts = Number(config.pay_attempts);
          if (config.force_pay !== undefined) forcePay = !!config.force_pay;
        } catch (parseErr) {
          console.error('Error parseando JSON de CONFIG_IA_MEJORAR_DESC', parseErr);
        }
      }

      // 2. Generar lista de intentos (true = usar clave de pago, false = clave gratuita)
      const attemptsList: boolean[] = [];
      if (forcePay) {
        for (let i = 0; i < payAttempts; i++) {
          attemptsList.push(true);
        }
      } else {
        for (let i = 0; i < freeAttempts; i++) {
          attemptsList.push(false);
        }
        for (let i = 0; i < payAttempts; i++) {
          attemptsList.push(true);
        }
      }
      if (attemptsList.length === 0) {
        attemptsList.push(false);
      }

      let res = null;
      let success = false;
      const totalAttempts = attemptsList.length;

      for (let idx = 0; idx < totalAttempts; idx++) {
        const usePayKey = attemptsList[idx];
        const attempt = idx + 1;
        if (usePayKey) {
          this.aiAttemptStatus = `Intento ${attempt}/${totalAttempts} (Pago)...`;
        } else {
          this.aiAttemptStatus = `Intento ${attempt}/${totalAttempts}...`;
        }

        try {
          res = await firstValueFrom(
            this.javapiService.mejorarDescripcion(
              this.currentItem.descripcion,
              systemInstruction,
              model,
              temperature,
              usePayKey,
              attempt,
              totalAttempts
            )
          );

          if (res && res.enhancedText) {
            success = true;
            break; // Salida del bucle si fue exitoso
          } else if (res && res.error) {
            console.warn(`Intento ${attempt}/${totalAttempts} fallido con error de IA:`, res.error);
          } else {
            console.warn(`Intento ${attempt}/${totalAttempts} fallido. Respuesta inválida.`);
          }
        } catch (attemptErr) {
          console.error(`Error en intento ${attempt}/${totalAttempts}:`, attemptErr);
        }

        // Esperar 1 segundo antes del siguiente intento
        if (attempt < totalAttempts) {
          await new Promise(r => setTimeout(r, 1000));
        }
      }

      if (!success) {
        throw new Error(res && res.error ? res.error : `No se pudo mejorar la descripción tras ${totalAttempts} intentos.`);
      }

      if (res && res.enhancedText) {
        this.improvedDescText = res.enhancedText;
        this.selectedDescMode = 'ai';
        this.currentItem.descripcion = this.improvedDescText;
        await this.metodosUi.showToast('Descripción mejorada con IA cargada.', 'success');
      }
    } catch (err: any) {
      console.error('Error al mejorar descripción:', err);
      await this.metodosUi.showToast(err?.message || 'Error al mejorar la descripción con IA', 'danger');
    } finally {
      this.isImprovingDesc = false;
      this.aiAttemptStatus = 'Mejorar con IA';
    }
  }

  toggleDescMode(mode: 'original' | 'ai') {
    // Si el usuario modificó manualmente el textarea, guardamos el cambio en la versión actualmente activa
    if (this.selectedDescMode === 'original') {
      this.originalDescText = this.currentItem.descripcion;
    } else if (this.selectedDescMode === 'ai') {
      this.improvedDescText = this.currentItem.descripcion;
    }

    this.selectedDescMode = mode;

    if (mode === 'original') {
      this.currentItem.descripcion = this.originalDescText;
    } else {
      this.currentItem.descripcion = this.improvedDescText;
    }
  }
}
